/**
 * One screening in full: Cora's verdict criterion by criterion, the note exactly as posted (or as it
 * would be posted, in shadow), the old Cora's verdict on the same candidate, what happened next in
 * Lever — and the two things a person can do: say whether Cora got it right, and re-run her.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { CoraApiError, fetchCoraRun, rerunCora, type CoraMode, type CoraRun, type Criterion } from '../../services/coraApi';
import { CRITERION_EMOJI, STATUS_LABEL, fmtSeconds, fmtUsd, fmtWhen, leverUrl } from './coraFormat';
import { RecommendationChip } from './ScreeningsTab';
import FeedbackForm from './FeedbackForm';
import { useCoraWrite } from './useCoraWrite';

const RESUME_SOURCE: Record<string, string> = {
  pdf: 'PDF',
  image: 'Image (scan or photo)',
  docx: 'Word document',
  'lever-parse': "Lever's CV parse (the file itself could not be read)",
};

function CriteriaTable({ title, rows }: { title: string; rows: Criterion[] }) {
  if (!rows.length) return null;
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-zinc-900">{title}</h3>
      <ul className="flex flex-col gap-2">
        {rows.map((c, i) => (
          <li key={`${i}-${c.criterion}`} className="rounded-sm border border-solid border-pearl p-2 text-sm">
            <div className="flex gap-2">
              <span aria-label={c.status}>{CRITERION_EMOJI[c.status]}</span>
              <span className="font-medium text-zinc-900">{c.criterion}</span>
              <span className="ml-auto whitespace-nowrap text-xs text-zinc-500">{c.evidence}{c.qualifier ? ` · ${c.qualifier}` : ''}</span>
            </div>
            <p className="mt-1 text-zinc-600">{c.detail}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Facts({ run }: { run: CoraRun }) {
  const facts: [string, string][] = [
    ['Position', `${run.positionTitle} (#${run.positionId})`],
    ['Status', STATUS_LABEL[run.status]],
    ['Mode', run.mode === 'live' ? 'Live — written to Lever' : 'Shadow — not written to Lever'],
    ['Screened', fmtWhen(run.finishedAt || run.startedAt)],
    ['CV read from', RESUME_SOURCE[run.resumeSource] ?? '—'],
    ['Model', `${run.model || '—'} · ${run.promptVersion}`],
    ['Cost / time', `${fmtUsd(run.costUsd || 0)} · ${fmtSeconds(run.durationMs)}`],
  ];
  if (run.writes) facts.push(['Lever writes', `note ${run.writes.note} · tags ${run.writes.tags}`]);
  if (run.outcome) facts.push(['Next step in Lever', run.outcome.archivedReason ? 'Archived' : run.outcome.stageId]);
  if (run.triggeredBy && run.triggeredBy !== 'schedule') facts.push(['Screened on request of', run.triggeredBy]);
  if (run.rerunBy) facts.push(['Re-run by', run.rerunBy]);
  return (
    <dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-1 text-sm">
      {facts.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-zinc-500">{k}</dt>
          <dd className="text-zinc-900">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function RerunButton({ run, mode, onDone, onModeChanged }: { run: CoraRun; mode: CoraMode | null; onDone: () => void; onModeChanged: () => void }) {
  const canWrite = useCoraWrite();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const notePosted = run.mode === 'live' && ['ok', 'existing-note-kept'].includes(run.writes?.note ?? '');
  const finishing = run.status === 'write_failed';
  if (!mode || run.mode !== mode || run.status === 'running') return null;
  // Live re-runs write to Lever and need Cora Operator access; the proxy enforces the same.
  if (mode === 'live' && !canWrite) return null;
  const rerun = async () => {
    setBusy(true);
    void logAnalyticsEvent('cora_rerun_clicked', { status: run.status, second_note: notePosted && !finishing, finish_writes: finishing });
    try {
      const result = await rerunCora(run.id, mode, notePosted && !finishing);
      void logAnalyticsEvent('cora_rerun_succeeded', { status: result.status });
      showToast({ status: 'success', description: `Cora re-ran this candidate: ${STATUS_LABEL[result.status as CoraRun['status']] ?? result.status}.` });
      setConfirming(false);
      onDone();
    } catch (err) {
      if (err instanceof CoraApiError && err.code === 'mode-changed') onModeChanged();
      void logAnalyticsEvent('cora_rerun_failed');
      showToast({ status: 'error', description: err instanceof Error ? err.message : 'The re-run failed.' });
    } finally {
      setBusy(false);
    }
  };
  if (finishing) {
    return (
      <Button variant="outline" onClick={rerun} disabled={busy}>
        {busy ? 'Finishing…' : 'Finish Lever writes'}
      </Button>
    );
  }
  if (notePosted && confirming) {
    return (
      <span className="flex flex-wrap items-center gap-2 text-sm text-orange">
        This posts a second Cora note in Lever and replaces her verdict tag.
        <Button variant="primary" onClick={rerun} disabled={busy}>{busy ? 'Re-running…' : 'Post second note'}</Button>
        <Button variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>Cancel</Button>
      </span>
    );
  }
  return (
    <Button variant="outline" onClick={notePosted ? () => setConfirming(true) : rerun} disabled={busy}>
      {busy ? 'Re-running…' : 'Re-run Cora'}
    </Button>
  );
}

export default function RunDetail({ runId, mode, onClose }: { runId: string; mode: CoraMode | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ['cora', 'run', runId], queryFn: () => fetchCoraRun(runId) });
  const run = data?.run;
  const refresh = () => {
    void refetch();
    void queryClient.invalidateQueries({ queryKey: ['cora', 'runs'] });
  };

  return (
    <Modal open onOpenChange={(next: boolean) => !next && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,56rem)] gap-5 overflow-auto rounded p-6">
        <ModalHeader>
          <ModalTitle>{run ? run.candidateName || 'Candidate' : 'Screening'}</ModalTitle>
        </ModalHeader>
        {isLoading && <p className="text-sm text-zinc-600">Loading…</p>}
        {isError && <p role="alert" className="text-sm text-red">{(error as Error).message}</p>}
        {run && (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-3">
              <RecommendationChip value={run.recommendation} />
              {run.salaryMismatch && <span className="text-sm text-orange">Salary mismatch</span>}
              <a className="text-sm text-trustedBlue underline" href={leverUrl(run.opportunityId)} target="_blank" rel="noreferrer">
                Open in Lever
              </a>
              <span className="ml-auto"><RerunButton run={run} mode={mode} onDone={refresh} onModeChanged={() => void queryClient.invalidateQueries({ queryKey: ['cora', 'status'] })} /></span>
            </div>
            {run.error && <p role="alert" className="rounded-sm border border-solid border-red p-3 text-sm text-red">{run.error}</p>}
            {run.ruleCheck && !run.ruleCheck.agrees && (
              <p className="rounded-sm border border-solid border-orange p-3 text-sm text-orange">
                By Cora&apos;s own rules these criteria point to “{run.ruleCheck.implied}”, but she chose “{run.recommendation}”. Worth a look.
              </p>
            )}
            {run.baseline && (
              <p className="rounded-sm border border-solid border-pearl p-3 text-sm text-zinc-700">
                Old Cora (Relevance) said: <strong>{run.baseline.recommendation || 'no verdict found in its note'}</strong>
                {run.baseline.recommendation && run.baseline.recommendation !== run.recommendation ? ' — they disagree.' : ' — they agree.'}
              </p>
            )}
            <Facts run={run} />
            {run.noResumeReason && <p className="text-sm text-zinc-700">Not assessed: {run.noResumeReason}.</p>}
            {run.assessment && (
              <>
                <CriteriaTable title="Must-haves" rows={run.assessment.mustHaves} />
                <CriteriaTable title="Nice-to-haves" rows={run.assessment.niceToHaves} />
              </>
            )}
            <details className="rounded-sm border border-solid border-pearl p-3">
              <summary className="cursor-pointer text-sm font-medium text-zinc-900">
                {run.mode === 'live' ? 'Note posted to Lever' : 'Note Cora would post'}
              </summary>
              <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-zinc-700">{run.note}</pre>
            </details>
            <FeedbackForm runId={run.id} feedback={run.feedback ?? []} onSaved={refresh} />
          </div>
        )}
      </ModalContent>
    </Modal>
  );
}
